import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ClientLayoutWithOutHeader from "../../client/components/layout/ClientLayoutWithOutHeaderForTeenPatti";
import TpStudioHeader from "../../client/tp-studio/tp-studio-header";
import TPStudioSidebar from "../../client/tp-studio/TPStudioSidebar";
import "../../../assets/topstudio/styles/CardsSprite.css";
import "../../../assets/topstudio/styles/aviator.css";
import useSocket from "../../../hooks/useSocket";
import axiosInstance from "../../../utils/axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Explosion from "../../../assets/topstudio/images/aviator/aviatorexplosion.gif";
import MainN from "../../../assets/topstudio/images/blastoff/pluto-bg-1.webp";
import Newplane from "../../../assets/topstudio/images/blastoff/pluto-ships.png";
import RepeatN from "../../../assets/topstudio/images/blastoff/pluto-bg-2.webp";
import BgSound from "../../../assets/topstudio/audio/aviatorbg.mp3";
import CrashSound from "../../../assets/topstudio/audio/Explosion.wav";
import Flame from "../../../assets/topstudio/images/blastoff/flame.png";
import AvaitorResult from "../../client/tp-studio/tp-result/AvaitorResult.jsx";

import { useDispatch, useSelector } from "react-redux";
import { setUserData } from "../../../store/userSlice";
import { Check, X } from "lucide-react";

import BlastoffRules from "./pop-up/blastoff-rules.jsx";
import "./Aviator.scoped.css";

/** Timing & flight settings */
const STEP = 0.01; // per tick
const STEP_MS = 91; // ms per tick after 1.10
const SLOW_UNTIL = 1.1;
const CASHOUT_MIN_MULTIPLIER = 1;
const SLOW_DURATION_MS = 2000; // 1.00 → 1.10
const SLOW_RANGE_STEPS = Math.round((SLOW_UNTIL - 1) / STEP);
const SLOW_STEP_MS = SLOW_DURATION_MS / SLOW_RANGE_STEPS;
const EXPLOSION_MS = 900; // blast duration
const DROP_MS = 3000; // plane drop animation length (kept in CSS as well)
const CLOSED_RESET_MS = 2000; // reset counter 2s after crash/CLOSED
const SERVER_TICK_MS = 1000; // filler uses 200ms then 91ms. This is only the first gap.
const LUCKY_PLAN_TARGET = 3.5; // dummy cashouts without knowing the crash

const FUN_FACT_OVERLAY_TEXT =
  "FunFact: A player cashed out 4.5 lakhs from a single bet.";

function formatWinToastAmount(amount) {
  const n = parseFloat(amount);
  if (Number.isNaN(n)) return "0";
  if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
  return n.toFixed(2);
}

function showWinToast(amount) {
  const display = formatWinToastAmount(amount);
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <Check size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">You won {display}</span>
    </div>,
    {
      toastId: "blastoff-win-toast",
      className: "av-win-toast",
      icon: false,
      closeButton: false,
      autoClose: 1500,
      hideProgressBar: false,
      position: "top-center"
    }
  );
}

function showLostToast(amount) {
  const display = formatWinToastAmount(amount);
  toast.dismiss("blastoff-win-toast");
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <X size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">You lost {display}</span>
    </div>,
    {
      toastId: `blastoff-lost-${Date.now()}`,
      className: "av-lost-toast",
      icon: false,
      closeButton: false,
      autoClose: 1500,
      hideProgressBar: false,
      position: "top-center"
    }
  );
}

function showBetPlacedToast() {
  toast(
    <div className="av-toast-body">
      <span className="av-toast-icon">
        <Check size={24} color="#ffffff" strokeWidth={3} aria-hidden="true" />
      </span>
      <span className="av-toast-text">Order Placed</span>
    </div>,
    {
      toastId: "blastoff-bet-placed-toast",
      className: "av-bet-toast",
      icon: false,
      closeButton: false,
      autoClose: 1000,
      hideProgressBar: false,
      position: "top-right"
    }
  );
}

const normalizeResultsList = (data) => {
  if (data == null) return [];
  const values = Array.isArray(data) ? data : Object.values(data);
  return values
    .filter((v) => v !== null && v !== undefined && v !== "")
    .map((v) => parseFloat(v))
    .filter((v) => !Number.isNaN(v));
};

const LAUNCH_COUNTDOWN_DEFAULT = 4;
const PRE_FLIGHT_STREAM_WINDOW_MS = (LAUNCH_COUNTDOWN_DEFAULT + 2) * 1000;

const RESULT_SYNC_BUFFER = 0.02;

const getTotalFlightSteps = (target) =>
  Math.max(1, Math.round((target - 1) / STEP) + 1);

/** Where the flight clock should stand for a given multiplier (2s slow start, then 91ms ticks). */
const elapsedMsFromMultiplier = (value, skipSlowStart) => {
  const v = Math.max(1, Number(value) || 1);
  if (skipSlowStart) {
    const steps = Math.max(0, Math.round((v - 1) / STEP));
    return steps * STEP_MS;
  }
  if (v <= SLOW_UNTIL + 0.0001) {
    const steps = Math.max(0, Math.round((v - 1) / STEP));
    return steps * SLOW_STEP_MS;
  }
  const extraSteps = Math.max(0, Math.round((v - SLOW_UNTIL) / STEP));
  return SLOW_DURATION_MS + extraSteps * STEP_MS;
};

/** Inverse of elapsedMsFromMultiplier — same curve as PHP aviatorElapsedToMultiplier. */
const multiplierFromElapsed = (elapsedMs, skipSlowStart) => {
  const ms = Math.max(0, Number(elapsedMs) || 0);
  if (skipSlowStart) {
    return 1 + Math.floor(ms / STEP_MS) * STEP;
  }
  if (ms <= SLOW_DURATION_MS) {
    const steps = Math.min(SLOW_RANGE_STEPS, Math.floor(ms / SLOW_STEP_MS));
    return 1 + steps * STEP;
  }
  const extraSteps = Math.floor((ms - SLOW_DURATION_MS) / STEP_MS);
  return SLOW_UNTIL + extraSteps * STEP;
};

/** Never exceed crash result; stay a hair below when joining near the end */
const clampSyncedFlightValue = (value, target) => {
  let v = Math.min(Math.max(1, value), target);
  const safeCeiling = +(target - RESULT_SYNC_BUFFER).toFixed(2);
  if (safeCeiling >= 1 && v > safeCeiling) {
    v = safeCeiling;
  }
  return +v.toFixed(2);
};

const blastOffValueToStep = (value, target) => {
  const totalSteps = getTotalFlightSteps(target);
  const step = Math.round((value - 1) / STEP) + 1;
  return Math.min(totalSteps, Math.max(1, step));
};

const MUSLIM_NAMES = [
  "Muhammad", "Ahmed", "Ali", "Hassan", "Hussein", "Omar", "Usman", "Bilal",
  "Hamza", "Yusuf", "Ibrahim", "Khalid", "Tariq", "Imran", "Farhan", "Saeed",
  "Rashid", "Nadeem", "Asif", "Faisal", "Zubair", "Salman", "Waqas", "Junaid",
  "Aamir", "Shahid", "Noman", "Rehan", "Sajid", "Kamran", "Arif", "Javed",
  "Fatima", "Aisha", "Zainab", "Maryam", "Khadija", "Hafsa", "Sana", "Hina",
  "Rabia", "Nadia", "Samina", "Shazia", "Farah", "Sadia", "Ayesha", "Bushra",
  "Uzma", "Nazia", "Saima", "Rubina", "Tahira", "Yasmin", "Amna", "Laiba",
  "Mahnoor", "Hira", "Kinza", "Aliza", "Danish", "Haroon", "Majid", "Rizwan"
];

const luckyRandomInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const maskLuckyPlayerName = (name) => {
  if (!name) return "****";
  const visible = name.slice(0, Math.min(4, name.length));
  return `${visible}****`;
};

const formatLuckyPlayerAmount = (value) =>
  Number(value).toLocaleString("en-IN");

/** profit = (size × price) − size */
const calcLuckyPlayerProfit = (price, amountRaw) => {
  const priceNum = parseFloat(price);
  const amount = Number(amountRaw);
  if (!Number.isFinite(priceNum) || !Number.isFinite(amount) || amount <= 0) {
    return "";
  }
  const profit = priceNum * amount - amount;
  return formatLuckyPlayerAmount(Math.round(profit));
};

const LUCKY_RESERVE_FRACTION = 0.12;
const LUCKY_MIN_RESERVE = 8;
const LUCKY_MAX_RESERVE = 25;
const LUCKY_MIN_STEP_GAP = 5;

const luckyBetIsOpen = (bet) =>
  !bet.locked && (bet.rate === "--" || bet.profit === "" || bet.profit == null);

const pickRandomOpenBetIds = (bets, count, reserveCount = 0) => {
  const open = bets.filter(luckyBetIsOpen);
  const maxPickable = Math.max(0, open.length - reserveCount);
  if (maxPickable <= 0) return new Set();
  const n = Math.min(count, maxPickable);
  const shuffled = [...open].sort(() => Math.random() - 0.5);
  return new Set(shuffled.slice(0, n).map((bet) => bet.id));
};

const applyRandomLuckyCashoutBatch = (bets, price, batchSize, reserveCount = 0) => {
  const selectedIds = pickRandomOpenBetIds(bets, batchSize, reserveCount);
  if (!selectedIds.size) return bets;
  return bets.map((bet) =>
    selectedIds.has(bet.id) ? lockLuckyBetAtPrice(bet, price) : bet
  );
};

const applyLuckyCashoutEvents = (bets, events, reserveCount) =>
  events.reduce(
    (acc, event) =>
      applyRandomLuckyCashoutBatch(
        acc,
        event.value.toFixed(2),
        event.batchSize,
        reserveCount
      ),
    bets
  );

const blastOffStepToFlightValue = (step, target) =>
  clampSyncedFlightValue(+(1 + step * STEP).toFixed(2), target);

/** Spread cashouts evenly across the flight; leave a slice of bets blank until the end */
const buildLuckyCashoutPlan = (target, totalBets) => {
  const reserveCount = Math.min(
    totalBets - 1,
    Math.max(
      LUCKY_MIN_RESERVE,
      Math.min(LUCKY_MAX_RESERVE, Math.ceil(totalBets * LUCKY_RESERVE_FRACTION))
    )
  );
  const toCashOut = Math.max(0, totalBets - reserveCount);
  const totalSteps = getTotalFlightSteps(target);
  const startStep = Math.max(1, Math.floor(totalSteps * 0.08));
  const endStep = Math.max(startStep + 1, Math.floor(totalSteps * 0.9));
  const usableSteps = endStep - startStep;
  const maxWaves = Math.max(1, Math.floor(usableSteps / LUCKY_MIN_STEP_GAP));
  const waveCount = Math.min(toCashOut, maxWaves);

  if (waveCount <= 0 || toCashOut <= 0) {
    return { events: [], reserveCount, nextEventIndex: 0 };
  }

  const baseBatch = Math.floor(toCashOut / waveCount);
  let remainder = toCashOut % waveCount;
  const events = [];

  for (let i = 0; i < waveCount; i++) {
    const step =
      waveCount === 1
        ? Math.floor((startStep + endStep) / 2)
        : startStep + Math.round((i / (waveCount - 1)) * usableSteps);
    const value = blastOffStepToFlightValue(step, target);
    let batchSize = baseBatch + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    if (batchSize > 0) events.push({ value, batchSize });
  }

  return { events, reserveCount, nextEventIndex: 0 };
};

const lockLuckyBetAtPrice = (bet, price) => {
  if (bet.locked) return bet;
  return {
    ...bet,
    rate: price,
    profit: calcLuckyPlayerProfit(price, bet.amountRaw),
    locked: true
  };
};

const createDummyLuckyBet = () => {
  const baseName =
    MUSLIM_NAMES[luckyRandomInt(0, MUSLIM_NAMES.length - 1)];
  const name = `${baseName}${luckyRandomInt(10, 99)}`;
  const amount = luckyRandomInt(3, 50) * 1000;

  return {
    id: `live-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    uname: maskLuckyPlayerName(name),
    amount: formatLuckyPlayerAmount(amount),
    amountRaw: amount,
    rate: "--",
    profit: ""
  };
};

const createDummyBetQueue = () => {
  const count = luckyRandomInt(100, 180);
  return Array.from({ length: count }, createDummyLuckyBet);
};

const parseLuckyPlayerEntry = (entry, { isUser = false, betId = null } = {}) => {
  try {
    const obj = typeof entry === "string" ? JSON.parse(entry) : entry;
    const id = betId || obj._id || obj.bet_id;
    if (!id) return null;

    const name = obj.uname || "";
    const amountRaw = Number(obj.lockamt || obj.delivery || obj.amount || 0);
    const rateVal = obj.rate;
    const rateNum = parseFloat(rateVal);
    const hasCashout =
      rateVal != null &&
      rateVal !== "" &&
      rateVal !== "-" &&
      rateVal !== "--" &&
      Number.isFinite(rateNum) &&
      rateNum > 1;
    const rate = hasCashout ? Number(rateNum).toFixed(2) : "--";
    const profit =
      hasCashout && amountRaw ? calcLuckyPlayerProfit(rate, amountRaw) : "";

    return {
      id: String(id),
      betId: id,
      sectionNo: obj.section_no,
      uname: maskLuckyPlayerName(name),
      amount: formatLuckyPlayerAmount(amountRaw),
      amountRaw,
      rate,
      profit,
      locked: hasCashout,
      isUser
    };
  } catch {
    return null;
  }
};

const buildUserRecordsFromReturnBets = (returnBets, amounts, displayName) =>
  returnBets
    .filter((bet) => bet?.bet_id)
    .map((returnBet, idx) => {
      const betId = String(returnBet.bet_id);
      const amountRaw = Number(amounts[idx] || 0);
      return {
        id: betId,
        betId,
        sectionNo: returnBet.section_no,
        uname: maskLuckyPlayerName(displayName),
        amount: formatLuckyPlayerAmount(amountRaw),
        amountRaw,
        rate: "--",
        profit: "",
        locked: false,
        isUser: true
      };
    });

export default function BalstOff() {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.user);
  const { data, isConnected } = useSocket({
    marketId: null,
    mtype: "teenpatti",
    type: "blastoff_virtual"
  });
  //console.log("data: ", data);
  // Parse socket payload
  const rawInner = data?.data;
  const payload = rawInner?.data?.evt_id
    ? rawInner.data
    : rawInner?.evt_id
      ? rawInner
      : null;

  const payloadResult = data?.data?.result;
  const userName = userInfo?.data?.uname || data?.user?.uname || "You";
  const payloadLuckyPlayers = rawInner?.data?.evt_id
    ? rawInner.lucky_players
    : data?.lucky_players;

  const [activeTabIndex, setActiveTabIndex] = useState(1);
  const [resultData, setResultData] = useState([]);
  const sessionUser = useSelector((s) => s.userStore.data);

  const lastTenResults = useMemo(
    () => normalizeResultsList(resultData).slice(0, 10),
    [resultData]
  );

  const [showRules, setShowRules] = useState(false);
  const activeTab = async (tab1) => {
    setActiveTabIndex(tab1);
    if (tab1 == 2) {
    }
  };
  useEffect(() => {
    if (payloadResult !== null && payloadResult !== undefined) {
      setResultData(payloadResult);
    }
  }, [payloadResult]);

  let betfairOdds = null;
  if (
    data != null &&
    data.data?.data !== null &&
    data.data?.data !== undefined
  ) {
    betfairOdds = data.data.data;
  }

  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [isAnimationDone, setIsAnimationDone] = useState(false);

  /// animation ///
  const [open, setOpen] = useState(false);
  const [scene, setScene] = useState("loading");
  const [launchCountdown, setLaunchCountdown] = useState(4);
  const sceneRef = useRef("loading");
  const lastCrashedEvtIdRef = useRef(null);
  const lastFinishedEvtIdRef = useRef(null);
  const lastFlightStartedEvtIdRef = useRef(null);
  const lastLuckyPlayersEvtIdRef = useRef(null);
  const lastCountdownResetEvtRef = useRef(null);
  const betPlacedToastEvtRef = useRef(null);
  const isLoadGameRef = useRef(0);
  const closedResetRef = useRef(null);
  const crashTimeoutRef = useRef(null);
  const luckyStreamTimerRef = useRef(null);
  const luckyCashoutTimerRef = useRef(null);
  const luckyCashoutPlanRef = useRef(null);
  const luckyPendingQueueRef = useRef([]);
  const userPlacedBetIdsRef = useRef(new Set());
  const userPlacedBetIdsOrderRef = useRef([]);

  const [userLuckyRecords, setUserLuckyRecords] = useState([]);
  const [liveBets, setLiveBets] = useState([]);
  const betsList = useMemo(
    () => [...userLuckyRecords, ...liveBets],
    [userLuckyRecords, liveBets]
  );
  const luckyPlayerCount = betsList.length;

  /** Flight state */
  const [running, setRunning] = useState(false);
  const [flightValue, setFlightValue] = useState(0);

  /** Explosion/drop flags (visual helpers) */
  const [showExplosion, setShowExplosion] = useState(false);
  const [isDropping, setIsDropping] = useState(false);
  const [isAudioOmitted, setIsAudioOmitted] = useState(false);

  /** Audio */
  const bgAudioRef = useRef(null);
  const crashAudioRef = useRef(null);

  /** RAF helpers */
  const rafRef = useRef(null);
  const startTsRef = useRef(null);
  const lastStepRef = useRef(-1);
  const skipStartHoldRef = useRef(false);
  /** Server running_result is the live cap; it only becomes the crash value once CLOSED publishes result */
  const crashAtRef = useRef(null);
  const crashKnownRef = useRef(false);
  const lastServerRunningRef = useRef(1);
  const counterRawRef = useRef(null);
  const capTsRef = useRef(0);
  const capFromRef = useRef(1);
  const capIntervalRef = useRef(SERVER_TICK_MS);
  const flightValueRef = useRef(0);
  const cashOutNowRef = useRef(null);
  const placeAllBetsNowRef = useRef(null);
  const isAudioOmittedRef = useRef(false);
  const betAmountRef = useRef(100);
  const mainBetIdRef = useRef("");
  const betGameIdRef = useRef("");
  const isCashingOutRef = useRef(false);
  const lostToastEvtRef = useRef(null);
  const pendingLostToastEvtRef = useRef(null);

  const [scrollVersion, setScrollVersion] = useState(0);

  /** Track disconnection so we can reset on reconnect */
  const wasDisconnectedRef = useRef(false);

  const updateUserData = async (userData) => {
    if (!userData || !sessionUser) return;
    await dispatch(
      setUserData({
        ...sessionUser,
        fname: userData.fname ?? sessionUser.fname,
        uname: userData.uname ?? sessionUser.uname,
        bz_balance: userData.bz_balance ?? sessionUser.bz_balance,
        opin_bal: userData.opin_bal ?? sessionUser.opin_bal,
        user_role: sessionUser.user_role,
        _id: sessionUser._id
      })
    );
  };

  const clearLuckyPlayersStream = () => {
    if (luckyStreamTimerRef.current) {
      clearTimeout(luckyStreamTimerRef.current);
      luckyStreamTimerRef.current = null;
    }
    if (luckyCashoutTimerRef.current) {
      clearInterval(luckyCashoutTimerRef.current);
      luckyCashoutTimerRef.current = null;
    }
  };

  const isInFlightScene = () =>
    sceneRef.current === "flight" ||
    sceneRef.current === "crash-explode" ||
    sceneRef.current === "crash-drop" ||
    sceneRef.current === "finished";

  const getPreFlightStreamDelay = (remainingCount) => {
    const base = Math.max(
      20,
      Math.floor(PRE_FLIGHT_STREAM_WINDOW_MS / Math.max(remainingCount, 1))
    );
    return luckyRandomInt(Math.max(15, base - 10), base + 20);
  };

  const flushRemainingDummyBets = () => {
    const queue = luckyPendingQueueRef.current;
    if (!queue.length) return;
    luckyPendingQueueRef.current = [];
    setLiveBets((prev) => [...queue, ...prev]);
  };

  const stopDummyBetStream = () => {
    if (luckyStreamTimerRef.current) {
      clearTimeout(luckyStreamTimerRef.current);
      luckyStreamTimerRef.current = null;
    }
  };

  const finalizeDummyBetsBeforeFlight = () => {
    stopDummyBetStream();
    flushRemainingDummyBets();
  };

  const scheduleNextLiveBet = () => {
    if (isInFlightScene()) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const queue = luckyPendingQueueRef.current;
    if (!queue.length) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const batchSize = luckyRandomInt(2, 5);
    const batch = queue.splice(0, Math.min(batchSize, queue.length));
    setLiveBets((prev) => [...batch, ...prev]);

    if (!queue.length) {
      luckyStreamTimerRef.current = null;
      return;
    }

    const delay = getPreFlightStreamDelay(queue.length);
    luckyStreamTimerRef.current = setTimeout(scheduleNextLiveBet, delay);
  };

  const startLiveLuckyPlayersStream = () => {
    clearLuckyPlayersStream();
    luckyPendingQueueRef.current = createDummyBetQueue();
    userPlacedBetIdsRef.current = new Set();
    userPlacedBetIdsOrderRef.current = [];
    setUserLuckyRecords([]);
    setLiveBets([]);
    scheduleNextLiveBet();
  };

  const resetToWaiting = () => {
    closedResetRef.current = null;
    if (crashTimeoutRef.current) {
      clearTimeout(crashTimeoutRef.current);
      crashTimeoutRef.current = null;
    }
    setRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setFlightValue(0);
    setShowExplosion(false);
    setIsDropping(false);
    crashAtRef.current = null;
    crashKnownRef.current = false;
    lastServerRunningRef.current = 1;
    startTsRef.current = null;
    counterRawRef.current = null;
    capTsRef.current = 0;
    capFromRef.current = 1;
    capIntervalRef.current = SERVER_TICK_MS;
    setScrollVersion((v) => v + 1);
    setScene("waiting");
    sceneRef.current = "waiting";
    isLoadGameRef.current = 0;
  };

  const beginFlight = ({ runningResult, force = false }) => {
    const evtId = roundIdRef.current;
    const postCrashScenes = ["crash-explode", "crash-drop", "finished"];

    if (evtId && lastFinishedEvtIdRef.current === evtId) return;
    if (evtId && lastCrashedEvtIdRef.current === evtId) return;
    if (evtId && lastFlightStartedEvtIdRef.current === evtId) return;
    if (!force && postCrashScenes.includes(sceneRef.current)) return;

    lastFlightStartedEvtIdRef.current = evtId;
    luckyCashoutPlanRef.current = null;
    finalizeDummyBetsBeforeFlight();

    crashKnownRef.current = false;
    const running = parseFloat(runningResult);
    const startValue = Number.isFinite(running) && running >= 1 ? running : 1;
    lastServerRunningRef.current = startValue;
    crashAtRef.current = startValue;

    const isLateJoin = Number.isFinite(running) && running > 1.15;
    skipStartHoldRef.current = !!isLateJoin;

    capTsRef.current = performance.now();
    capFromRef.current = startValue;
    capIntervalRef.current = SERVER_TICK_MS;

    if (isLateJoin) {
      flightValueRef.current = startValue;
      counterRawRef.current = startValue;
      setFlightValue(startValue);
      lastStepRef.current = blastOffValueToStep(startValue, startValue);
      startTsRef.current =
        performance.now() -
        elapsedMsFromMultiplier(startValue, skipStartHoldRef.current);
    } else {
      flightValueRef.current = 1;
      counterRawRef.current = 1;
      setFlightValue(1);
      lastStepRef.current = -1;
      startTsRef.current = null;
    }

    setScrollVersion((v) => v + 1);
    setScene("flight");
    sceneRef.current = "flight";
    setRunning(true);
  };

  const startTakeoffAfterBets = async ({ runningResult, force = false }) => {
    const evtId = roundIdRef.current;
    const evtStatus = payload?.evt_status ?? payload?.status;
    if (evtStatus === "CLOSED") return;
    const leftTime = Number(payload?.left_time);
    // A 3-day-old Redis row reports start==1 and a 16.xx cap. Do not join it.
    if (Number.isFinite(leftTime) && leftTime > 300) return;
    if (evtId && lastFinishedEvtIdRef.current === evtId) return;
    if (evtId && lastCrashedEvtIdRef.current === evtId) return;
    if (!force && evtId && lastFlightStartedEvtIdRef.current === evtId) return;

    beginFlight({ runningResult, force });
    if (isAudioOmittedRef.current) {
      bgAudioRef.current?.play()?.catch(() => { });
    } else {
      bgAudioRef.current?.pause();
    }
  };

  // ===== Socket reconnect handling =====
  useEffect(() => {
    if (!isConnected) {
      wasDisconnectedRef.current = true;
      // Pause the flight ticker while disconnected
      setRunning(false);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    // Socket just came back online
    if (wasDisconnectedRef.current) {
      wasDisconnectedRef.current = false;
      //console.log("Socket reconnected – resetting BlastOff state");

      // Reset the game to the loading state so server data drives the next scene
      setFlightValue(0);
      setShowExplosion(false);
      setIsDropping(false);
      setScene("loading");
      sceneRef.current = "loading";
      isLoadGameRef.current = 0;
      lastCrashedEvtIdRef.current = null;
      lastFinishedEvtIdRef.current = null;
      //added by uk - the socket drop left this round marked as already flying, so the rocket never started again.
      lastFlightStartedEvtIdRef.current = null;
      crashAtRef.current = null;
      crashKnownRef.current = false;
      lastServerRunningRef.current = 1;
      counterRawRef.current = null;
      capTsRef.current = 0;
      capFromRef.current = 1;
      capIntervalRef.current = SERVER_TICK_MS;
      setScrollVersion((v) => v + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // ===== Betting state =====
  const [betAmount, setBetAmount] = useState(100);
  const [betErrorMessage, setBetErrorMessage] = useState({});
  const [betButtonShow, setBetButtonShow] = useState(true);
  const [betArray, setBetArray] = useState([]);
  const betArrayRef = useRef([]);
  const [mainBetId, setMainBetId] = useState("");
  const [cashOutAmount, setCashOutAmount] = useState("");
  const [showCashoutButton, setShowCashoutButton] = useState(false);
  const [isCashingOut, setIsCashingOut] = useState(false);
  const betPlacedRef = useRef(false);
  const [betGameId, setBetGameId] = useState("");

  // ===== Auto Bet / Auto Cashout =====
  const [autoBet, setAutoBet] = useState(false);
  const [autoCashout, setAutoCashout] = useState(false);
  const [autoCashoutAt, setAutoCashoutAt] = useState(1.1);
  const autoBetRef = useRef(false);
  const autoCashoutRef = useRef(false);
  const autoCashoutAtRef = useRef(1.1);
  isAudioOmittedRef.current = isAudioOmitted;

  const isPostCrashScene = () => {
    const s = sceneRef.current;
    return s === "crash-explode" || s === "crash-drop" || s === "finished";
  };

  const tryAutoCashoutAtValue = (currentValue) => {
    const value = Number(currentValue);
    if (!(value + 0.0001 >= CASHOUT_MIN_MULTIPLIER)) return;
    if (
      !autoCashoutRef.current ||
      !betPlacedRef.current ||
      isCashingOutRef.current
    )
      return;
    const autoAt = parseFloat(autoCashoutAtRef.current);
    if (!(autoAt > 0) || value + 0.0001 < autoAt) return;
    cashOutNowRef.current?.(autoAt);
  };

  const showLostToastForUncashedBets = (evtId = roundIdRef.current) => {
    if (evtId && lostToastEvtRef.current === evtId) return;
    if (!betPlacedRef.current || isCashingOutRef.current) return;
    const lostAmount = Number(betAmountRef.current) || 0;
    if (lostAmount <= 0) return;
    if (evtId) lostToastEvtRef.current = evtId;
    pendingLostToastEvtRef.current = null;
    showLostToast(lostAmount);
  };

  // ===== Shared state =====
  const [roundId, setRoundId] = useState("");
  const roundIdRef = useRef("");

  // ===== Sync payload -> state (bet lifecycle) =====
  useEffect(() => {
    if (!payload) return;
    if (payload.evt_id && payload.evt_id === lastFinishedEvtIdRef.current)
      return;

    updateUserData(data?.user);

    if (payload.evt_id) {
      setRoundId(payload.evt_id);
      roundIdRef.current = payload.evt_id;
    }

    const evtStatus = payload.evt_status ?? payload.status;

    // start == 0 → NEW ROUND / COUNTDOWN
    if (payload.start == 0) {
      const incomingEvtId = payload.evt_id;
      const flyingThisRound =
        incomingEvtId &&
        lastFlightStartedEvtIdRef.current &&
        String(lastFlightStartedEvtIdRef.current) === String(incomingEvtId) &&
        (sceneRef.current === "flight" ||
          sceneRef.current === "crash-explode" ||
          sceneRef.current === "crash-drop");
      // A late start==0 for the round that already took off would hide Cashout
      // for about a second and then bring it back. Ignore it.
      if (flyingThisRound) {
        return;
      }
      const isNewCountdownRound =
        incomingEvtId && incomingEvtId !== lastCountdownResetEvtRef.current;

      // The next evt can open while this client is still flying the last one
      // (missed CLOSED tick). Close that flight against the old id so it cannot
      // mark the new round finished.
      if (isNewCountdownRound && sceneRef.current === "flight") {
        const flyingEvtId = lastFlightStartedEvtIdRef.current;
        if (flyingEvtId && flyingEvtId !== incomingEvtId) {
          lastFinishedEvtIdRef.current = flyingEvtId;
          lastCrashedEvtIdRef.current = flyingEvtId;
          const result = crashAtRef.current;
          if (result != null && result > 0) {
            setResultData((prev) => {
              const list = normalizeResultsList(prev);
              if (list.length > 0 && list[list.length - 1] === result) {
                return prev;
              }
              return [...list, result];
            });
          }
          pendingLostToastEvtRef.current = flyingEvtId;
          showLostToastForUncashedBets(flyingEvtId);
        }
        setRunning(false);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      }

      lastFlightStartedEvtIdRef.current = null;
      if (lastCrashedEvtIdRef.current === incomingEvtId) {
        lastCrashedEvtIdRef.current = null;
      }
      crashAtRef.current = null;
      crashKnownRef.current = false;
      lastServerRunningRef.current = 1;
      startTsRef.current = null;
      counterRawRef.current = null;
      capTsRef.current = 0;
      capFromRef.current = 1;
      capIntervalRef.current = SERVER_TICK_MS;
      if (closedResetRef.current) {
        clearTimeout(closedResetRef.current);
        closedResetRef.current = null;
      }
      if (crashTimeoutRef.current) {
        clearTimeout(crashTimeoutRef.current);
        crashTimeoutRef.current = null;
      }

      // Transition from loading/waiting to countdown on new evt_id
      if (
        sceneRef.current === "loading" ||
        sceneRef.current === "waiting" ||
        sceneRef.current === "finished" ||
        sceneRef.current === "crash-explode" ||
        sceneRef.current === "crash-drop" ||
        sceneRef.current === "flight"
      ) {
        setLaunchCountdown(LAUNCH_COUNTDOWN_DEFAULT);
        setFlightValue(0);
        setShowExplosion(false);
        setIsDropping(false);
        setScrollVersion((v) => v + 1);
        setScene("countdown");
        sceneRef.current = "countdown";
      }

      if (isNewCountdownRound) {
        lastCountdownResetEvtRef.current = payload.evt_id;
        betPlacedToastEvtRef.current = null;
        lostToastEvtRef.current = null;

        setBetButtonShow(true);
        setShowCashoutButton(false);
        setMainBetId("");
        mainBetIdRef.current = "";
        setCashOutAmount("");
        betPlacedRef.current = false;
        setBetGameId("");
        betGameIdRef.current = "";
        if (betArrayRef.current.length === 0) setBetErrorMessage({});
      }

      if (
        payload.evt_id &&
        payload.evt_id !== lastLuckyPlayersEvtIdRef.current
      ) {
        lastLuckyPlayersEvtIdRef.current = payload.evt_id;
        startLiveLuckyPlayersStream();
      }

      if (
        isNewCountdownRound &&
        autoBetRef.current &&
        betArrayRef.current.length === 0 &&
        !betPlacedRef.current
      ) {
        handleBetClick();
      }

      // Bets can only be taken before takeoff, so flush anything still queued
      // as soon as the betting window opens.
      if (isNewCountdownRound) {
        const flushQueued =
          betArrayRef.current.length > 0 && !betPlacedRef.current;
        if (flushQueued) {
          setBetErrorMessage({ status: true, message: "Placing bet..." });
          placeAllBetsNowRef.current?.();
        }
      }
      // start == 1 → FLIGHT IN PROGRESS
    } else if (payload.start == 1 && evtStatus !== "CLOSED") {
      // Join from running_result — the crash is not on the live feed.
      if (
        (sceneRef.current === "loading" || sceneRef.current === "waiting") &&
        lastFlightStartedEvtIdRef.current !== payload.evt_id
      ) {
        startTakeoffAfterBets({ runningResult: payload.running_result });
      }

      if (
        !isCashingOutRef.current &&
        (betArrayRef.current.length > 0 || betPlacedRef.current)
      ) {
        setBetButtonShow(false);
      }

      isLoadGameRef.current = 1;

      if (
        !isCashingOutRef.current &&
        betPlacedRef.current &&
        (mainBetIdRef.current || mainBetId)
      ) {
        setShowCashoutButton(true);
      }
    }
    if (payload.start == 0) isLoadGameRef.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  // Server running_result is a hard ceiling. Each new value gives the ticker a
  // fresh window to animate into, so the counter moves without ever passing it.
  useEffect(() => {
    if (!payload || payload.start != 1) return;
    const evtStatus = payload.evt_status ?? payload.status;
    if (evtStatus === "CLOSED") return;
    const srv = parseFloat(payload.running_result);
    if (!Number.isFinite(srv) || srv < 1) return;

    const nowTs = performance.now();
    const shown = Number.isFinite(counterRawRef.current)
      ? counterRawRef.current
      : 1;
    if (capTsRef.current > 0 && srv > lastServerRunningRef.current + 0.0001) {
      const measured = nowTs - capTsRef.current;
      if (measured >= 20 && measured <= 1500) {
        capIntervalRef.current = measured;
      }
    }
    capTsRef.current = nowTs;
    //added by uk - do not jump straight to the new server number.
    //added by uk - start from the number already on screen and climb up to it. Same filler as Aviator.
    capFromRef.current = Math.min(shown, srv);
    lastServerRunningRef.current = srv;
    crashAtRef.current = srv;
  }, [
    payload?.running_result,
    payload?.start,
    payload?.evt_status,
    payload?.evt_id
  ]);

  // CLOSED -> crash. The public result is the only thing that kills the rocket.
  useEffect(() => {
    const status = payload?.evt_status ?? payload?.status;
    const evtId = payload?.evt_id;
    if (
      status === "CLOSED" &&
      evtId &&
      lastCrashedEvtIdRef.current !== evtId &&
      (sceneRef.current === "flight" ||
        sceneRef.current === "countdown" ||
        sceneRef.current === "loading" ||
        sceneRef.current === "waiting")
    ) {
      const result = parseFloat(payload?.result);
      if (!Number.isFinite(result) || result <= 0) return; // wait until result is public

      lastCrashedEvtIdRef.current = evtId;
      crashKnownRef.current = true;
      crashAtRef.current = result;
      lastServerRunningRef.current = Math.max(
        lastServerRunningRef.current || 1,
        result
      );

      const shown = Number.isFinite(counterRawRef.current)
        ? counterRawRef.current
        : 1;
      const alreadyShowingCrash = shown + 0.001 >= result;
      const stillOnCountdown = sceneRef.current === "countdown";
      const joiningClosedRound =
        sceneRef.current === "loading" || sceneRef.current === "waiting";

      if (
        stillOnCountdown ||
        alreadyShowingCrash ||
        joiningClosedRound ||
        sceneRef.current === "flight"
      ) {
        //added by uk - when the round closes, show the crash right away.
        counterRawRef.current = result;
        flightValueRef.current = result;
        lastStepRef.current = result;
        setFlightValue(result);
        triggerCrash();
      } else {
        capFromRef.current = shown;
        capTsRef.current = performance.now();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload?.evt_status, payload?.status, payload?.evt_id, payload?.result]);

  // Keep sceneRef in sync
  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  useEffect(() => {
    startLiveLuckyPlayersStream();
    return () => clearLuckyPlayersStream();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const placedOrder = userPlacedBetIdsOrderRef.current;
    if (
      !placedOrder.length ||
      !payloadLuckyPlayers ||
      typeof payloadLuckyPlayers !== "object"
    ) {
      return;
    }

    setUserLuckyRecords((prev) => {
      const next = placedOrder
        .map((betId) => {
          const existing = prev.find(
            (bet) => String(bet.betId) === String(betId)
          );
          if (existing?.locked) return existing;

          const entry = payloadLuckyPlayers[betId];
          if (entry) {
            const parsed = parseLuckyPlayerEntry(entry, {
              isUser: true,
              betId
            });
            if (parsed) {
              if (sceneRef.current !== "flight" || !parsed.locked) {
                return {
                  ...parsed,
                  rate: "--",
                  profit: "",
                  locked: false
                };
              }
              return parsed;
            }
          }
          return existing;
        })
        .filter(Boolean);

      return next.length ? next : prev;
    });
  }, [payloadLuckyPlayers]);

  useEffect(() => {
    flightValueRef.current = flightValue;
  }, [flightValue]);

  useEffect(() => {
    betAmountRef.current = betAmount;
  }, [betAmount]);

  useEffect(() => {
    mainBetIdRef.current = mainBetId;
  }, [mainBetId]);

  useEffect(() => {
    isCashingOutRef.current = isCashingOut;
  }, [isCashingOut]);

  useEffect(() => {
    if (scene !== "flight" || flightValue < 1) {
      if (scene !== "flight") luckyCashoutPlanRef.current = null;
      return;
    }

    if (!luckyCashoutPlanRef.current) {
      setLiveBets((prev) => {
        if (prev.length > 0) {
          luckyCashoutPlanRef.current = buildLuckyCashoutPlan(
            LUCKY_PLAN_TARGET,
            prev.length
          );
        }
        return prev;
      });
      if (!luckyCashoutPlanRef.current) return;
    }

    const plan = luckyCashoutPlanRef.current;
    const pendingEvents = [];

    while (
      plan.nextEventIndex < plan.events.length &&
      flightValue + 0.0001 >= plan.events[plan.nextEventIndex].value
    ) {
      pendingEvents.push(plan.events[plan.nextEventIndex]);
      plan.nextEventIndex += 1;
    }

    if (!pendingEvents.length) return;

    setLiveBets((prev) =>
      applyLuckyCashoutEvents(prev, pendingEvents, plan.reserveCount)
    );
  }, [scene, flightValue]);

  // Show cashout during flight
  useEffect(() => {
    if (scene !== "flight") return;
    if (
      !isCashingOutRef.current &&
      betPlacedRef.current &&
      (mainBetIdRef.current || mainBetId)
    ) {
      setShowCashoutButton(true);
    }
  }, [scene, mainBetId]);

  // Auto-cashout
  useEffect(() => {
    if (scene !== "flight" || flightValue < CASHOUT_MIN_MULTIPLIER) return;
    tryAutoCashoutAtValue(Math.min(flightValue, lastServerRunningRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightValue, scene, showCashoutButton, isCashingOut]);

  // Update cashout amount in real-time
  useEffect(() => {
    if (scene === "flight" && flightValue > 0) {
      if (showCashoutButton && betAmount > 0)
        setCashOutAmount((parseFloat(betAmount) * flightValue).toFixed(2));
    }
  }, [flightValue, scene, showCashoutButton, betAmount]);

  // ===== Bet functions =====
  const hasValidPayload = !!(payload && payload.evt_id);

  const handleBetClick = useCallback(() => {
    if (!betAmount || betAmount <= 0) return;
    const liveOdds = Number(payload?.b1);
    const betObj = {
      amount: Number(betAmount),
      profit: 0,
      catmid: payload?.evt_id || roundIdRef.current,
      round: payload?.evt_id || roundIdRef.current,
      odds: Number.isFinite(liveOdds) && liveOdds > 0 ? liveOdds : 1.95,
      teamname: "blastoff",
      bettype: "b",
      marketVal: 1,
      game_type: 7,
      market_type: "m",
      game_name: "blastoff"
    };
    betArrayRef.current = [
      ...betArrayRef.current,
      { ...betObj, is_bet: false }
    ];
    setBetArray([...betArrayRef.current]);

    // The server refuses bets once the rocket has taken off, so send it right
    // away while the betting window is still open.
    if (payload?.start == 0) {
      setBetErrorMessage({ status: true, message: "Placing bet..." });
      placeAllBetsNowRef.current?.();
    } else {
      setBetErrorMessage({
        status: true,
        message: "Previous round running. Bet queued"
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betAmount, payload]);

  const placeAllBetsNow = async () => {
    const placedEvtId = roundIdRef.current;
    const bets = [...betArrayRef.current];
    betArrayRef.current = [];
    setBetArray([]);
    const combinedBetArray = [];
    let baseBet = null;
    for (const bet of bets) {
      if (!bet.is_bet) {
        bet.is_bet = true;
        bet.catmid = roundIdRef.current || bet.catmid;
        bet.round = roundIdRef.current || bet.round;
        baseBet = bet;
        combinedBetArray.push({
          bet_type: bet.game_type,
          bet_amount: bet.amount,
          section_no: 0
        });
      }
    }
    if (combinedBetArray.length === 0 || !baseBet) return;
    //added by uk - do not lock the next bet behind this request. Waiting made it arrive after takeoff.

    const liveOdds = Number(baseBet?.odds);
    const requestBody = {
      ...baseBet,
      amount: Number(baseBet?.amount),
      teamname: "blastoff",
      bettype: baseBet?.bettype || "b",
      market_type: "m",
      game_name: "blastoff",
      odds: Number.isFinite(liveOdds) && liveOdds > 0 ? liveOdds : 1.95,
      catmid: roundIdRef.current || baseBet.catmid,
      round: roundIdRef.current || baseBet.round,
      bet_array: combinedBetArray
    };

    try {
      const response = await axiosInstance.post(
        "/casino/place-bet-casino",
        requestBody
      );
      if (response.data?.status != null) {
        if (response.data.status) {
          const returnBets = response.data?.data?.return_bets;
          const betsArray = Array.isArray(returnBets)
            ? returnBets
            : returnBets?.bet_id
              ? [returnBets]
              : [];
          let anyBetConfirmed = false;
          if (betsArray[0]?.bet_id) {
            mainBetIdRef.current = betsArray[0].bet_id;
            betGameIdRef.current = roundIdRef.current || roundId;
            setMainBetId(betsArray[0].bet_id);
            setBetGameId(roundIdRef.current || roundId);
            betPlacedRef.current = true;
            setShowCashoutButton(true);
            setBetButtonShow(false);
            anyBetConfirmed = true;

            if (
              roundIdRef.current &&
              betPlacedToastEvtRef.current !== roundIdRef.current
            ) {
              betPlacedToastEvtRef.current = roundIdRef.current;
              showBetPlacedToast();
              setBetErrorMessage({});
            }
          }

          const placedAmounts = combinedBetArray.map((b) => b.bet_amount);
          const userRecords = buildUserRecordsFromReturnBets(
            betsArray,
            placedAmounts,
            userName
          );
          if (userRecords.length) {
            userPlacedBetIdsOrderRef.current = userRecords.map(
              (record) => record.betId
            );
            userPlacedBetIdsRef.current = new Set(
              userPlacedBetIdsOrderRef.current.map(String)
            );
            setUserLuckyRecords(userRecords);
          }

          if (anyBetConfirmed) {
            const roundAlreadyOver =
              isPostCrashScene() ||
              pendingLostToastEvtRef.current === placedEvtId ||
              lastFinishedEvtIdRef.current === placedEvtId;
            if (roundAlreadyOver) {
              showLostToastForUncashedBets(placedEvtId);
            } else {
              tryAutoCashoutAtValue(flightValueRef.current);
            }
          }
        } else {
          setBetErrorMessage({ status: false, message: response.data.message });
        }
      }
    } catch (error) {
      setBetErrorMessage({
        status: false,
        message: error.response?.data?.message || "Bet placement failed"
      });
    }
  };
  placeAllBetsNowRef.current = placeAllBetsNow;

  const cancelBet = () => {
    betArrayRef.current = [];
    setBetArray([]);
    setBetErrorMessage({ status: false, message: "Bet cancelled" });
  };

  const cashOutNow = async (multiplierOverride) => {
    const currentBetId = mainBetIdRef.current || mainBetId;
    if (!currentBetId || isCashingOutRef.current) return;

    const rawMultiplier =
      multiplierOverride != null && Number(multiplierOverride) > 0
        ? Number(multiplierOverride)
        : Number(flightValueRef.current);
    if (!(rawMultiplier + 0.0001 >= CASHOUT_MIN_MULTIPLIER)) return;
    const incrementor = rawMultiplier.toFixed(2);
    const gameId =
      betGameIdRef.current || betGameId || roundIdRef.current || roundId;
    const cashedBetId = currentBetId;

    setIsCashingOut(true);
    isCashingOutRef.current = true;
    setShowCashoutButton(false);
    setBetButtonShow(true);
    setCashOutAmount("");

    try {
      const response = await axiosInstance.post("/casino/cash-out", {
        game_id: gameId,
        bet_id: cashedBetId,
        gametype: "blastoff",
        win_multiplier: incrementor
      });
      const result = response.data;
      if (result.isSuccess && result.error === 0) {
        const wonAmount =
          result.data?.cash_out_amount ?? result.cash_out_amount;
        if (wonAmount != null && wonAmount !== "") {
          showWinToast(wonAmount);
        }

        setBetErrorMessage({});
        setShowCashoutButton(false);
        setBetButtonShow(true);
        mainBetIdRef.current = "";
        setMainBetId("");
        setCashOutAmount("");
        betPlacedRef.current = false;
        betArrayRef.current = [];
        setBetArray([]);

        const cashedAmount = parseFloat(betAmountRef.current || betAmount);
        setUserLuckyRecords((prev) =>
          prev.map((bet) => {
            if (String(bet.betId) !== String(cashedBetId) || bet.locked) {
              return bet;
            }
            const amount = bet.amountRaw || cashedAmount;
            return {
              ...bet,
              rate: incrementor,
              profit: calcLuckyPlayerProfit(incrementor, amount),
              locked: true
            };
          })
        );
      } else {
        const crashAt = Number(crashAtRef.current);
        const requested = parseFloat(incrementor);
        const cashedAtOrBelowCrash =
          Number.isFinite(crashAt) &&
          Number.isFinite(requested) &&
          requested <= crashAt + 0.001;
        if (isPostCrashScene() && !cashedAtOrBelowCrash) {
          isCashingOutRef.current = false;
          showLostToastForUncashedBets();
        }
        const stillFlying = sceneRef.current === "flight";
        setShowCashoutButton(stillFlying);
        if (stillFlying) setBetButtonShow(false);
        setBetErrorMessage({
          status: false,
          message: result.message || "Cashout failed"
        });
      }
    } catch (error) {
      const crashAt = Number(crashAtRef.current);
      const requested = parseFloat(incrementor);
      const cashedAtOrBelowCrash =
        Number.isFinite(crashAt) &&
        Number.isFinite(requested) &&
        requested <= crashAt + 0.001;
      if (isPostCrashScene() && !cashedAtOrBelowCrash) {
        isCashingOutRef.current = false;
        showLostToastForUncashedBets();
      }
      const stillFlying = sceneRef.current === "flight";
      setShowCashoutButton(stillFlying);
      if (stillFlying) setBetButtonShow(false);
      setBetErrorMessage({
        status: false,
        message: error.response?.data?.message || "Cashout request failed"
      });
    } finally {
      isCashingOutRef.current = false;
      setIsCashingOut(false);
    }
  };
  cashOutNowRef.current = cashOutNow;

  /** ===== Countdown (independent from explosion) ===== */
  useEffect(() => {
    if (scene !== "countdown") return;

    // Take off the moment the server does, even if the local countdown has time
    // left. Short rounds are over in a second and cannot wait for it.
    if (payload?.start == 1) {
      // Don't replay a round that already finished — wait for a new evt_id
      if (payload?.evt_id && payload.evt_id === lastFinishedEvtIdRef.current)
        return;
      // Don't start flight if the current event is already CLOSED
      const evtStatus = payload?.evt_status ?? payload?.status;
      if (evtStatus === "CLOSED") return;
      if (lastFlightStartedEvtIdRef.current === payload?.evt_id) return;

      startTakeoffAfterBets({ runningResult: payload?.running_result });
      return;
    }

    if (launchCountdown <= 0) return; // wait for the server to start the round

    const t = setTimeout(() => setLaunchCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [scene, launchCountdown, payload?.start, payload?.running_result]);

  /** ===== Flight ticker (only during "flight") ===== */
  useEffect(() => {
    if (scene !== "flight" || !running) return;

    let countingStarted = false;

    const tick = (now) => {
      const evtId = roundIdRef.current;
      if (evtId && lastFinishedEvtIdRef.current === evtId) {
        setRunning(false);
        return;
      }

      const crashTarget = crashAtRef.current;

      // First tick — fresh flight or mid-round join
      if (!countingStarted) {
        countingStarted = true;
        if (startTsRef.current == null) startTsRef.current = now;
        if (lastStepRef.current < 0) lastStepRef.current = 0;
      }

      const cap = lastServerRunningRef.current;
      const safeCap = Number.isFinite(cap) && cap >= 1 ? cap : 1;

      const from = Math.max(
        1,
        Number.isFinite(capFromRef.current) ? capFromRef.current : safeCap
      );
      const sinceCap = Math.max(0, now - (capTsRef.current || now));
      //added by uk - filler. Walk one cent at a time up to the server number.
      //added by uk - 200ms per cent until 1.10, then 91ms. Same speed as Aviator.
      const cents = Math.max(0, Math.round((safeCap - from) * 100));
      const stepMs = safeCap <= 1.1 ? 200 : 91;
      const catchWindowMs = Math.max(40, cents * stepMs);
      const progress = Math.min(1, sinceCap / catchWindowMs);
      let raw = from + (safeCap - from) * progress;
      raw = Math.min(safeCap, raw);

      const prevRaw = counterRawRef.current;
      if (Number.isFinite(prevRaw) && prevRaw > raw) raw = Math.min(prevRaw, safeCap);

      counterRawRef.current = raw;
      const next = Math.max(1, Math.floor(raw * 100) / 100);
      flightValueRef.current = next;

      tryAutoCashoutAtValue(Math.min(next, safeCap));

      // Hold at running_result until CLOSED publishes result. Do not crash on the cap.
      if (crashKnownRef.current && next + 0.001 >= (crashTarget || safeCap)) {
        const finalValue = crashTarget || safeCap;
        lastStepRef.current = finalValue;
        flightValueRef.current = finalValue;
        setFlightValue(finalValue);
        triggerCrash();
        return;
      }

      if (next !== lastStepRef.current) {
        lastStepRef.current = next;
        setFlightValue(next);
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, running]);

  /** ===== Separate crash pipeline ===== */
  const triggerCrash = () => {
    if (
      sceneRef.current === "crash-explode" ||
      sceneRef.current === "crash-drop"
    ) {
      return;
    }

    const result = Number(crashAtRef.current);
    const shown = Number(flightValueRef.current);
    const crashDisplay =
      Number.isFinite(result) && result >= 1
        ? result
        : Number.isFinite(shown) && shown >= 1
          ? shown
          : null;
    if (crashDisplay == null) return;

    // Stop flight and RAF
    setRunning(false);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const crashedEvtId =
      lastFlightStartedEvtIdRef.current || roundIdRef.current;
    if (crashedEvtId) {
      lastFinishedEvtIdRef.current = crashedEvtId;
      lastCrashedEvtIdRef.current = crashedEvtId;
    }

    crashAtRef.current = crashDisplay;
    counterRawRef.current = crashDisplay;
    flightValueRef.current = crashDisplay;
    lastStepRef.current = crashDisplay;
    setFlightValue(crashDisplay);
    setResultData((prev) => {
      const list = normalizeResultsList(prev);
      if (list.length > 0 && list[list.length - 1] === crashDisplay)
        return prev;
      return [...list, crashDisplay];
    });

    // Explosion phase
    setScene("crash-explode");
    sceneRef.current = "crash-explode";
    setShowExplosion(true);
    try {
      crashAudioRef.current?.play()?.catch(() => { });
    } catch {
      // ignore
    }

    tryAutoCashoutAtValue(crashDisplay);
    pendingLostToastEvtRef.current = crashedEvtId;
    showLostToastForUncashedBets(crashedEvtId);

    if (closedResetRef.current) clearTimeout(closedResetRef.current);
    closedResetRef.current = setTimeout(resetToWaiting, CLOSED_RESET_MS);

    // After blast time, switch to drop — only if this crash is still showing
    if (crashTimeoutRef.current) clearTimeout(crashTimeoutRef.current);
    crashTimeoutRef.current = setTimeout(() => {
      crashTimeoutRef.current = null;
      if (sceneRef.current !== "crash-explode") return;
      setShowExplosion(false);
      setIsDropping(true);
      setScene("crash-drop");
      sceneRef.current = "crash-drop";
    }, EXPLOSION_MS);
  };

  /** ===== End of drop animation ===== */
  const handleDropEnd = () => {
    if (isDropping) {
      setIsDropping(false);
      setScene("finished");

      setTimeout(() => {
        setFlightValue(0);
        setShowExplosion(false);
        setIsDropping(false);

        // force remount of the scrolling track → resets CSS animation
        setScrollVersion((v) => v + 1);

        setScene("waiting");
        sceneRef.current = "waiting";
        isLoadGameRef.current = 0;
      }, 10);
    }
  };

  /** ===== Sound toggle ===== */
  const toggleSound = (e) => {
    const on = e.target.checked;
    if (on) bgAudioRef.current?.play()?.catch(() => { });
    else bgAudioRef.current?.pause();
  };

  useEffect(() => {
    if (scene !== "flight") return;
    const flame = document.getElementById("animated-svg");
    if (!flame) return;
    const interval = setInterval(() => {
      flame.classList.toggle("flame-animate-1");
    }, 1);

    return () => clearInterval(interval);
  }, [scene]);

  useEffect(() => {
    if (scene !== "crash-drop") return;
    const flame = document.getElementById("animated-svg");
    if (!flame) return;

    flame.classList.add("flame-out");
  }, [scene]);

  /** Convenience flags */
  useEffect(() => {
    document.documentElement.classList.add("aviator-bg-black");
    document.documentElement.classList.add("h-100");

    return () => {
      document.documentElement.classList.remove("aviator-bg-black");
      document.documentElement.classList.remove("h-100");
    };
  }, []);

  useEffect(() => {
    if (isConnected && data) {
      setIsPageLoaded(true);
    }
  }, [isConnected, data]);
  return (
    <>
      <div className="blastoff-scope aviator-scope aviator-bg-black">
        <ClientLayoutWithOutHeader showLoader={false}>
          <style>
            {`
            .blastoff-scope {
              width: 100%;
              max-width: 100%;
              overflow-x: hidden;
              box-sizing: border-box;
            }
            .blastoff-scope .details-tabs-wrapper1,
            .blastoff-scope .main-page,
            .blastoff-scope .bodyheight,
            .blastoff-scope .card-body {
              max-width: 100%;
              box-sizing: border-box;
            }
            @media (max-width:576px){
            .left-content {
              margin: 5px !important; 
            }
            .result-itemAV {
              padding: 5px !important;
            }
            .footer-mob{
                margin-left: 7px;
                margin-right: 7px;
              }
      
            .blastoff-scope .card-body{
            padding: 5px 0;
            width: 100%;
            overflow-x: hidden;
            }
            .blastoff-scope .card-body > .row.w-100 {
              flex-direction: column;
              width: 100%;
              margin-left: 0;
              margin-right: 0;
            }
            .blastoff-scope .card-body > .row.w-100 > .col-md-8,
            .blastoff-scope .card-body > .row.w-100 > .col-md-4 {
              flex: 0 0 100%;
              max-width: 100%;
              width: 100%;
              padding-left: 0 !important;
              padding-right: 0 !important;
            }
            .blastoff-scope .cards-layout.blackjack,
            .blastoff-scope .scroll-containeraviator {
              width: 100% !important;
              max-width: 100% !important;
            }
            .blastoff-scope .blastoff-bet-panel {
              width: 100% !important;
              max-width: 100% !important;
              flex: 0 0 100% !important;
              margin-left: 0;
              margin-right: 0;
            }
            .blastoff-scope .blastoff-bet-row {
              width: 100%;
              justify-content: stretch !important;
            }
            .blastoff-scope .scroll-containeraviator > .row {
              display: flex;
              flex-wrap: nowrap;
              align-items: flex-start;
            }
            .blastoff-scope .scroll-containeraviator > .row > .col-10 {
              flex: 1 1 auto;
              width: auto;
              max-width: none;
            }
            .blastoff-scope .flight-timer.timer-crash {
              top: 105px;
            }
           }

           .counter-num {
            position: absolute;
            top: 20%; left: 50%;
            transform: translate(-50%, -50%);
            font-size: 80px;
            font-weight: 700;
            color: #fff;
            z-index: 5;
            text-shadow: 0 0 20px rgba(0,0,0,0.5);
          }
   
            @media (min-width: 577px) {
            .blastoff-scope .blastoff-bet-panel {
              width: 50%;
              max-width: 50%;
              flex: 0 0 50%;
              margin-left: auto;
              margin-right: auto;
            }
            }
            .blastoff-scope .scroll-containeraviator .Connectedclients {
              position: relative;
              top: -8px;
              left: -24px;
            }
            .blastoff-scope .scroll-containeraviator .row > .col-1:last-child > div {
              position: relative !important;
              top: -10px;
              right: -24px;
              float: none;
            }
            .blastoff-scope .scroll-containeraviator .AVI-icon__signal-strength {
              transform: scale(0.9);
              transform-origin: center;
            }
            .blastoff-scope .scroll-containeraviator label.sound-icon[for="AvxSoundcontrol"] {
              transform: scale(0.76);
              transform-origin: bottom right;
              bottom: -33px !important;
              right: 19px !important;
            }
            `}
          </style>
          <div class="details-tabs-wrapper1">
            <div className="main-page">
              <div className="row no-gutters">
                <div className="col-md-12">
                  <div className="bodyheight">
                    <div className="wrapper">
                      <TPStudioSidebar open={open} />
                    </div>

                    <div className="lobby-ctn BPGmaindiv">
                      <TpStudioHeader setOpen={setOpen} open={open} />
                    </div>
                    <div className="card-body" style={{ position: "relative" }}>
                      {(!isPageLoaded || !isAnimationDone) && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            zIndex: 9999,
                            backgroundColor: "#000000",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          <style>{`
                            @keyframes blastoff-blink {
                              0%   { opacity: 1; }
                              50%  { opacity: 0; }
                              100% { opacity: 1; }
                            }
                            .blastoff-loading-img {
                              width: 35%;
                              object-fit: contain;
                              animation: blastoff-blink 1.2s ease-in-out 3;
                              animation-fill-mode: forwards;
                            }
                            @media (max-width: 576px) {
                              .blastoff-loading-img {
                                width: 90%;
                              }
                            }
                          `}</style>
                          <img
                            src="/assets/images/tpstudio/BlastOffLoading.png"
                            alt="Loading…"
                            className="blastoff-loading-img"
                            onAnimationEnd={() => setIsAnimationDone(true)}
                          />
                        </div>
                      )}
                      <div className="row w-100 blastoff-game-row">
                        <div className="col-12 col-md-8">
                          <div className="footer-divbpg border-gradient-footer border-gradient-purple-footer h-45 footer-mob">
                            <div className="col-6 col-md-6 titlebarname inline-block Gameidcol mt-2">
                              <p>BLASTOFF</p>
                            </div>
                            <div
                              className="col-6 col-md-6 inline-block Gameidcol text-right"
                              style={{ marginTop: "4px" }}
                            >
                              <div
                                role="group"
                                aria-label="Second group"
                                className="btn-group btn-group-sm mr-2"
                              >
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  id="Alltab"
                                  className={
                                    activeTabIndex == 1
                                      ? "tablink btn btn-primary bbactive"
                                      : "tablink btn btn-primary"
                                  }
                                  onClick={() => activeTab(1)}
                                >
                                  Game
                                </button>
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  className={
                                    activeTabIndex == 2
                                      ? "tablink btn btn-primary bbactive"
                                      : "tablink btn btn-primary"
                                  }
                                  onClick={() => activeTab(2)}
                                >
                                  Results
                                </button>
                                <button
                                  style={{ fontSize: "14px" }}
                                  type="button"
                                  className="tablink btn btn-primary"
                                  onClick={() => setShowRules(true)}
                                >
                                  Rules
                                </button>
                                <BlastoffRules
                                  show={showRules}
                                  onClose={() => setShowRules(false)}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Audio */}
                          <audio ref={bgAudioRef} src={BgSound} loop />
                          <audio ref={crashAudioRef} src={CrashSound} />

                          {/* Scene */}
                          <div
                            className="cards-layout blackjack font-familyAVE"
                            style={{
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            <div className="scroll-containeraviator">
                              <div
                                className="row"
                                style={{
                                  position: "absolute",
                                  width: "100%",
                                  padding: "10px"
                                }}
                              >
                                <div className="col-1">
                                  <div className="Connectedclients mt-1 ml-1 float-start">
                                    <i
                                      aria-hidden="true"
                                      className="fa fa-user mr-1"
                                      style={{ width: "14px", color: "white" }}
                                    />
                                    <b>
                                      <span id="UsersCount" className="number">
                                        {luckyPlayerCount}
                                      </span>
                                    </b>
                                  </div>
                                </div>
                                <div className="col-10"></div>
                                <div className="col-1">
                                  <div
                                    style={{
                                      float: "inline-end",
                                      position: "relative",
                                      zIndex: 1000
                                    }}
                                  >
                                    <i className="AVI-icon__signal-strength AVI-signal-0">
                                      <span
                                        className="AVI-bar-1"
                                        style={{
                                          backgroundColor: "rgb(255, 0, 0)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-2"
                                        style={{
                                          backgroundColor: "rgb(255, 0, 0)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-3"
                                        style={{
                                          backgroundColor: "rgb(204, 204, 204)"
                                        }}
                                      ></span>
                                      <span
                                        className="AVI-bar-4"
                                        style={{
                                          backgroundColor: "rgb(204, 204, 204)"
                                        }}
                                      ></span>
                                    </i>
                                  </div>
                                </div>
                              </div>

                              {/* Overlay: disconnected */}
                              {!isConnected && (
                                <div
                                  className="overlayaviator"
                                  style={{
                                    zIndex: 999,
                                    background: "rgba(0,0,0,0.85)"
                                  }}
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <span
                                      className="overlay-textaviator"
                                      style={{ color: "#ff4444" }}
                                    >
                                      <b>Connection Lost</b>
                                    </span>
                                    <br />
                                    <span
                                      className="overlay-textaviator"
                                      style={{
                                        fontSize: "14px",
                                        color: "#ccc"
                                      }}
                                    >
                                      Reconnecting...
                                    </span>
                                  </div>
                                </div>
                              )}

                              {/* Overlay: countdown */}
                              {isConnected && scene === "countdown" && (
                                <div className="overlayaviator">
                                  <div
                                    style={{
                                      textAlign: "center",
                                      width: "100%"
                                    }}
                                  >
                                    <span className="overlay-textaviator">
                                      <b>
                                        {launchCountdown > 0
                                          ? `Launching in ${launchCountdown}`
                                          : "Launching..."}
                                      </b>
                                    </span>
                                    <div className="aviator-fun-fact-banner">
                                      {FUN_FACT_OVERLAY_TEXT}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Flight timer */}
                              {(scene === "crash-explode" ||
                                scene === "crash-drop") &&
                                flightValue >= 1 && (
                                  <div className="flight-timer timer-crash">
                                    <div className="crash-text">CRASHED</div>
                                    {flightValue.toFixed(2)}x
                                  </div>
                                )}
                              {scene === "flight" && (
                                <div className="counter-num">
                                  {(flightValue >= 1 ? flightValue : 1).toFixed(
                                    2
                                  )}
                                  x
                                </div>
                              )}
                              {/* Scrolling terrain */}
                              <div id="toshakecontent" className="w-100 p-0">
                                <div
                                  key={scrollVersion}
                                  className={`scroll-contentaviator ${
                                    scene === "flight" ? "running" : "paused"
                                  }`}
                                >
                                  <img src={MainN} alt="slice-0" />
                                  {Array.from({ length: 18 }).map((_, i) => (
                                    <img
                                      key={i}
                                      src={RepeatN}
                                      alt={`slice-${i + 1}`}
                                    />
                                  ))}
                                  <img src={MainN} alt="slice-end" />
                                </div>
                              </div>

                              {/* Plane (always mounted).
                            During countdown, it's blurred and under the overlay. */}
                              <div
                                id="centerImageaviator"
                                className={`containerAVIM center-imageaviator ${
                                  scene === "countdown" ||
                                  scene === "waiting" ||
                                  scene === "loading"
                                    ? "blurred"
                                    : ""
                                }`}
                                style={{ zIndex: 5 }}
                              >
                                {/* Explosion sits above the plane but inside same container.
                              Shown only during crash-explode phase. */}

                                {scene === "crash-explode" && showExplosion && (
                                  <img src={Explosion} alt="Explosion" />
                                )}

                                {/* Plane wrapper: sways during flight, drops on crash-drop */}
                                <div
                                  className={`${
                                    scene === "flight" && !isDropping
                                      ? "plane-sway"
                                      : ""
                                  } ${
                                    isDropping ? "plane-drop grayscale" : ""
                                  }`}
                                  onAnimationEnd={
                                    isDropping ? handleDropEnd : undefined
                                  }
                                >
                                  <div id="planeimage" className="contentAVIM">
                                    {/* Thrust SVG */}

                                    {/* Plane */}
                                    <img
                                      src={Newplane}
                                      alt="Plane"
                                      className="w-36 h-36"
                                    />
                                    <img
                                      src={Flame}
                                      id="animated-svg"
                                      alt="Flame"
                                      className="flame-default"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Sound toggle */}
                              <label
                                htmlFor="AvxSoundcontrol"
                                className="fa fa-volume-off fa-inverse sound-icon fa-2x"
                                style={{
                                  position: "absolute",
                                  right: 8,
                                  bottom: 8,
                                  zIndex: 100
                                }}
                                title="Toggle sound"
                              >
                                <input
                                  id="AvxSoundcontrol"
                                  type="checkbox"
                                  onChange={toggleSound}
                                  style={{ display: "none" }}
                                />
                              </label>
                            </div>
                          </div>
                          {/* /Scene */}

                          <center
                            style={{
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            <div className="footer-divbpg border-gradient-footer border-gradient-purple-footer">
                              <div className="buttons-div">
                                {lastTenResults.length > 0
                                  ? lastTenResults.map((val, idx) => (
                                      <button
                                        key={idx}
                                        className="video-button border-gradient-btn border-gradient-purple-btn"
                                      >
                                        {val.toFixed(2)}
                                      </button>
                                    ))
                                  : null}
                              </div>
                              <div className="round-div">
                                <div className="label">Round ID:</div>
                                <div className="id">{roundId || "--"}</div>
                              </div>
                            </div>
                          </center>

                          <div
                            className="row m-0 p-0 d-flex align-item-center justify-content-center blastoff-bet-row"
                            style={{
                              backgroundColor: "#443135",
                              display: activeTabIndex == 1 ? "" : "none"
                            }}
                          >
                            {/* BET PANEL */}
                            <div
                              className="blastoff-bet-panel mb-4 p-1"
                              style={{
                                display: activeTabIndex == 1 ? "" : "none"
                              }}
                            >
                              <div className="market-runners">
                                <div className="row mb-2">
                                  <div className="col-12">
                                    <div className="input-group">
                                      <div className="AVEDI-input-group">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setBetAmount((v) =>
                                              Math.max(1, v - 100)
                                            )
                                          }
                                        >
                                          <svg
                                            width="28"
                                            height="40"
                                            viewBox="0 0 28 40"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="AVE-svg-icon"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20Z"
                                              fill="#F7F9FB"
                                            />
                                          </svg>
                                        </button>
                                        <input
                                          type="number"
                                          min="1"
                                          max="1000000"
                                          className="AVEDI-input-field"
                                          value={betAmount}
                                          onChange={(e) => {
                                            const val = e.target.value.replace(
                                              /[^0-9]/g,
                                              ""
                                            );
                                            setBetAmount(
                                              val === ""
                                                ? ""
                                                : Math.max(
                                                    1,
                                                    parseInt(val) || 0
                                                  )
                                            );
                                          }}
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setBetAmount((v) => v + 100)
                                          }
                                        >
                                          <svg
                                            width="28"
                                            height="40"
                                            viewBox="0 0 28 40"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="AVE-svg-icon"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              clipRule="evenodd"
                                              d="M14 11.75C14.1989 11.75 14.3897 11.829 14.5303 11.9697C14.671 12.1103 14.75 12.3011 14.75 12.5V19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H14.75V27.5C14.75 27.6989 14.671 27.8897 14.5303 28.0303C14.3897 28.171 14.1989 28.25 14 28.25C13.8011 28.25 13.6103 28.171 13.4697 28.0303C13.329 27.8897 13.25 27.6989 13.25 27.5V20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H13.25V12.5C13.25 12.3011 13.329 12.1103 13.4697 11.9697C13.6103 11.829 13.8011 11.75 14 11.75Z"
                                              fill="#F7F9FB"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="mb-2 increamentalbtns">
                                  <div className="col-6 col-md-3 mb-2 mb-md-0 p-0">
                                    <button
                                      type="button"
                                      className="btn btn-lg btn-outline-light w-100 p-0"
                                      onClick={() => setBetAmount(10000)}
                                    >
                                      10,000
                                    </button>
                                  </div>
                                  <div className="col-6 col-md-3 mb-2 mb-md-0 p-0">
                                    <button
                                      type="button"
                                      className="btn btn-lg btn-outline-light w-100 p-0"
                                      onClick={() => setBetAmount(250000)}
                                    >
                                      250,000
                                    </button>
                                  </div>
                                  <div className="col-6 col-md-3 mb-2 mb-md-0 p-0">
                                    <button
                                      type="button"
                                      className="btn btn-lg btn-outline-light w-100 p-0"
                                      onClick={() =>
                                        setBetAmount((v) => v + 100000)
                                      }
                                    >
                                      +100,000
                                    </button>
                                  </div>
                                  <div className="col-6 col-md-3 mb-2 mb-md-0 p-0">
                                    <button
                                      type="button"
                                      className="btn btn-lg btn-outline-light w-100 p-0"
                                      onClick={() =>
                                        setBetAmount((v) => v + 250000)
                                      }
                                    >
                                      +250,000
                                    </button>
                                  </div>
                                </div>

                                {/* Bet / Cancel / Cashout Button */}
                                <div className="row">
                                  <div className="col-12">
                                    {(() => {
                                      const showCancel =
                                        betArray.length > 0 &&
                                        !showCashoutButton;
                                      const showCashout =
                                        showCashoutButton &&
                                        scene === "flight" &&
                                        !isCashingOut;
                                      const showBet =
                                        !showCancel && !showCashout;
                                      return (
                                        <div
                                          role="group"
                                          className="btn-group w-100"
                                        >
                                          {showBet && (
                                            <button
                                              type="button"
                                              className="btn-lg btn btn-success w-100"
                                              onClick={handleBetClick}
                                              disabled={
                                                !betButtonShow ||
                                                !hasValidPayload
                                              }
                                            >
                                              <span className="shimmer-container">
                                                <span className="shimmer"></span>
                                              </span>
                                              <span className="text-lg font-semibold text-slate-50">
                                                {scene === "countdown" &&
                                                hasValidPayload
                                                  ? "Place Bet"
                                                  : "Next"}
                                              </span>
                                            </button>
                                          )}
                                          {showCancel && (
                                            <button
                                              type="button"
                                              className="btn-lg btn btn-info w-100"
                                              onClick={cancelBet}
                                            >
                                              <span className="text-lg font-semibold text-slate-50">
                                                Cancel
                                              </span>
                                            </button>
                                          )}
                                          {showCashout && (
                                            <button
                                              type="button"
                                              className="btn-lg btn btn-warning w-100"
                                              onClick={() => cashOutNow()}
                                              disabled={isCashingOut}
                                            >
                                              <span className="text-lg font-semibold">
                                                Cashout
                                                {cashOutAmount
                                                  ? " " + cashOutAmount
                                                  : ""}
                                              </span>
                                            </button>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>

                                {/* Bet error/success message */}
                                {betErrorMessage.message && (
                                  <div
                                    className={
                                      "alert " +
                                      (betErrorMessage.status
                                        ? "alert-success"
                                        : "alert-danger") +
                                      " m-0 p-1 mt-1"
                                    }
                                    style={{ fontSize: "11px" }}
                                  >
                                    {betErrorMessage.message}
                                  </div>
                                )}

                                <div
                                  className="mb-2"
                                  style={{
                                    color: "white",
                                    marginTop: "10px",
                                    paddingTop: "5px",
                                    border: "1px solid white",
                                    borderRadius: "10px",
                                    padding: "5px"
                                  }}
                                >
                                  <div className="col-12 p-0  d-flex align-items-center">
                                    <label className="switch">
                                      <input
                                        type="checkbox"
                                        checked={autoBet}
                                        onChange={(e) => {
                                          setAutoBet(e.target.checked);
                                          autoBetRef.current = e.target.checked;
                                        }}
                                      />
                                      <span className="slider round"></span>
                                    </label>
                                    <label>Auto Bet</label>
                                  </div>
                                  <div className="col-12 p-0 d-flex align-items-center">
                                    <label className="switch mr-2">
                                      <input
                                        type="checkbox"
                                        checked={autoCashout}
                                        onChange={(e) => {
                                          setAutoCashout(e.target.checked);
                                          autoCashoutRef.current =
                                            e.target.checked;
                                        }}
                                      />
                                      <span className="slider round"></span>
                                    </label>
                                    <label>Auto Cashout</label>
                                  </div>
                                  <div
                                    style={{
                                      border: "1px solid white",
                                      borderRadius: "10px",
                                      padding: "5px",
                                      display: autoCashout ? "block" : "none"
                                    }}
                                  >
                                    <div className="form-group">
                                      <label>Cashout at</label>
                                      <div className="input-group">
                                        <div className="AVEDI-input-group">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const v = Math.max(
                                                1.1,
                                                +(autoCashoutAt - 0.02).toFixed(
                                                  2
                                                )
                                              );
                                              setAutoCashoutAt(v);
                                              autoCashoutAtRef.current = v;
                                            }}
                                          >
                                            <svg
                                              width="28"
                                              height="40"
                                              viewBox="0 0 28 40"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="AVE-svg-icon"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20Z"
                                                fill="#F7F9FB"
                                              />
                                            </svg>
                                          </button>
                                          <input
                                            type="number"
                                            min="1.10"
                                            max="100"
                                            step="0.02"
                                            className="AVEDI-input-field"
                                            value={autoCashoutAt}
                                            onChange={(e) => {
                                              const v =
                                                parseFloat(e.target.value) ||
                                                1.1;
                                              setAutoCashoutAt(v);
                                              autoCashoutAtRef.current = v;
                                            }}
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const v = +(
                                                autoCashoutAt + 0.02
                                              ).toFixed(2);
                                              setAutoCashoutAt(v);
                                              autoCashoutAtRef.current = v;
                                            }}
                                          >
                                            <svg
                                              width="28"
                                              height="40"
                                              viewBox="0 0 28 40"
                                              fill="none"
                                              xmlns="http://www.w3.org/2000/svg"
                                              className="AVE-svg-icon"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M14 11.75C14.1989 11.75 14.3897 11.829 14.5303 11.9697C14.671 12.1103 14.75 12.3011 14.75 12.5V19.25H21.5C21.6989 19.25 21.8897 19.329 22.0303 19.4697C22.171 19.6103 22.25 19.8011 22.25 20C22.25 20.1989 22.171 20.3897 22.0303 20.5303C21.8897 20.671 21.6989 20.75 21.5 20.75H14.75V27.5C14.75 27.6989 14.671 27.8897 14.5303 28.0303C14.3897 28.171 14.1989 28.25 14 28.25C13.8011 28.25 13.6103 28.171 13.4697 28.0303C13.329 27.8897 13.25 27.6989 13.25 27.5V20.75H6.5C6.30109 20.75 6.11032 20.671 5.96967 20.5303C5.82902 20.3897 5.75 20.1989 5.75 20C5.75 19.8011 5.82902 19.6103 5.96967 19.4697C6.11032 19.329 6.30109 19.25 6.5 19.25H13.25V12.5C13.25 12.3011 13.329 12.1103 13.4697 11.9697C13.6103 11.829 13.8011 11.75 14 11.75Z"
                                                fill="#F7F9FB"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              display: activeTabIndex == 2 ? "" : "none"
                            }}
                          >
                            <AvaitorResult resultData={resultData} />
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="right-content">
                            <div>
                              <div className="table-box-body">
                                <div
                                  id="betSlip"
                                  className="bets"
                                  style={{ display: "none" }}
                                >
                                  <strong>
                                    Bet Slip{" "}
                                    <a
                                      target="_blank"
                                      href="/Customer/Profile"
                                      className="button"
                                      style={{ color: "white", float: "right" }}
                                      rel="noreferrer"
                                    >
                                      Edit Bet Sizes
                                    </a>
                                  </strong>

                                  {/* Betting table */}
                                  <div className="betting-table">
                                    <table className="table">
                                      <thead>
                                        <tr>
                                          <th>Bet for</th>
                                          <th>Odds</th>
                                          <th>Stake</th>
                                          <th>Profit</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr className="back">
                                          <td></td>
                                          <td width="10%">
                                            <div className="quantity">
                                              <input
                                                type="text"
                                                id="bet-price"
                                              />
                                              <div className="quantity-nav">
                                                <div className="quantity-button quantity-up">
                                                  <span className="fa fa-caret-up"></span>
                                                </div>
                                                <div className="quantity-button quantity-down">
                                                  <span className="fa fa-caret-down"></span>
                                                </div>
                                              </div>
                                            </div>
                                          </td>
                                          <td width="10%">
                                            <div className="stake">
                                              <input
                                                type="text"
                                                id="bet-size"
                                              />
                                            </div>
                                          </td>
                                          <td> / -</td>
                                        </tr>

                                        {/* Quick amount buttons */}
                                        <tr className="back">
                                          <td colSpan="5">
                                            <table className="table">
                                              <tbody>
                                                <tr className="checknow">
                                                  <td>
                                                    <span
                                                      data-amount="10000"
                                                      className="points"
                                                    >
                                                      10,000
                                                    </span>
                                                  </td>
                                                  <td>
                                                    <span className="points">
                                                      250,000
                                                    </span>
                                                  </td>
                                                  <td>
                                                    <span className="points">
                                                      50,000
                                                    </span>
                                                  </td>
                                                  <td>
                                                    <span className="points">
                                                      100,000
                                                    </span>
                                                  </td>
                                                </tr>
                                                <tr className="checknow">
                                                  <td>
                                                    <span className="points">
                                                      + 100,000
                                                    </span>
                                                  </td>
                                                  <td>
                                                    <span className="points">
                                                      + 250,000
                                                    </span>
                                                  </td>
                                                  <td>
                                                    <span className="points">
                                                      + 500,000
                                                    </span>
                                                  </td>
                                                  <td>
                                                    <span className="points">
                                                      + 1,000,000
                                                    </span>
                                                  </td>
                                                </tr>
                                                <tr>
                                                  <td>
                                                    <button
                                                      type="reset"
                                                      className="align-left btn btn-danger"
                                                    >
                                                      <b>Close</b>
                                                    </button>
                                                  </td>
                                                  <td>
                                                    <button
                                                      type="reset"
                                                      className="align-left btn btn-warning"
                                                    >
                                                      <b>Clear</b>
                                                    </button>
                                                  </td>
                                                  <td colSpan="1"></td>
                                                  <td>
                                                    <div
                                                      className="btn btn-primary ld-over"
                                                      style={{
                                                        cursor: "pointer"
                                                      }}
                                                    >
                                                      <b>Submit</b>
                                                      <div className="ld ld-ball ld-flip"></div>
                                                    </div>
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>

                                {/* Lucky Players list */}
                                <div className="bets">
                                  <strong>
                                    Lucky Players {luckyPlayerCount}
                                    <img
                                      src="/img/reconnecting.gif"
                                      alt="dc"
                                      className="rounded disconnected"
                                      style={{ display: "none" }}
                                    />
                                  </strong>
                                  <div className="betting-table aviator-lucky-players-scroll">
                                    <table className="table">
                                      <thead>
                                        <tr>
                                          <th>User</th>
                                          <th>Size</th>
                                          <th>Price</th>
                                          <th>Profit</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {betsList.length > 0 ? (
                                          betsList.map((bet) => (
                                            <tr
                                              key={bet.id}
                                              style={{
                                                backgroundColor: "aliceblue"
                                              }}
                                            >
                                              <td>{bet.uname}</td>
                                              <td>{bet.amount}</td>
                                              <td>{bet.rate}</td>
                                              <td style={{ textAlign: "left" }}>
                                                {bet.profit || "--"}
                                              </td>
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td
                                              colSpan="4"
                                              style={{
                                                textAlign: "center",
                                                color: "#888"
                                              }}
                                            >
                                              No players yet
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>{" "}
                        {/*  ends here  */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ClientLayoutWithOutHeader>
      </div>
      <ToastContainer
        position="top-center"
        autoClose={1500}
        hideProgressBar={false}
        closeButton={false}
        newestOnTop
        limit={1}
      />
      <style>
        {`
          .av-win-toast,
          .av-bet-toast,
          .av-lost-toast {
            border-radius: 8px !important;
            padding: 12px 20px !important;
            min-height: unset !important;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.38) !important;
            overflow: hidden;
          }
          .av-win-toast {
            background: #2d7a3a !important;
          }
          .av-bet-toast {
            background: #43a047 !important;
          }
          .av-lost-toast {
            background: #c62828 !important;
          }
          .av-win-toast .Toastify__toast-body,
          .av-bet-toast .Toastify__toast-body,
          .av-lost-toast .Toastify__toast-body {
            padding: 0 !important;
            margin: 0 !important;
          }
          .av-win-toast .Toastify__progress-bar,
          .av-bet-toast .Toastify__progress-bar,
          .av-lost-toast .Toastify__progress-bar {
            background: rgba(0, 0, 0, 0.2) !important;
            height: 4px;
          }
          .av-toast-body {
            display: flex;
            align-items: center;
            gap: 12px;
            color: #ffffff;
            font-size: 18px;
            font-weight: 600;
            line-height: 1.2;
            white-space: nowrap;
          }
          .av-toast-icon {
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
          }
          .av-win-toast-icon-svg,
          .av-toast-icon svg {
            display: block;
            width: 24px;
            height: 24px;
          }
          .av-toast-text {
            padding-top: 1px;
          }
        `}
      </style>
    </>
  );
}
